/**
 * Video review states.
 *
 * Video is the one media type where a bad upload cannot be un-seen, so every
 * clip enters review before it becomes viewable to the wider community. The
 * database decides the state; this module only describes it and tells the UI
 * which audience is allowed to watch.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export type ReviewState = "pending" | "approved" | "rejected";

export const REVIEW_COPY: Record<ReviewState, { label: string; detail: string }> = {
  pending: {
    label: "In review",
    detail: "This video is waiting for a moderator. Only you and moderators can watch it until then.",
  },
  approved: { label: "Reviewed", detail: "This video has been reviewed and is visible to its audience." },
  rejected: {
    label: "Not approved",
    detail: "A moderator turned this video down, so it stays hidden from everyone else.",
  },
};

export function normalizeReviewState(value: unknown): ReviewState {
  return value === "pending" || value === "rejected" ? value : "approved";
}

/**
 * Who may actually watch a clip: everyone for approved video, otherwise only
 * the person who posted it and moderators. The server re-checks this before it
 * ever hands out a playback link.
 */
export function canWatch(state: ReviewState, opts: { isOwner?: boolean; isReviewer?: boolean }) {
  return state === "approved" || Boolean(opts.isOwner) || Boolean(opts.isReviewer);
}

/** True while a badge should be shown next to the clip. */
export function showsReviewBadge(state: ReviewState) {
  return state !== "approved";
}

/** Is the signed-in member allowed to make review decisions? */
export function useIsMediaReviewer() {
  const { user } = useAuth();
  const [isReviewer, setIsReviewer] = useState(false);

  useEffect(() => {
    let active = true;
    if (!user) { setIsReviewer(false); return; }
    void supabase
      .rpc("is_media_reviewer", { _user: user.id })
      .then(({ data }) => { if (active) setIsReviewer(data === true); });
    return () => { active = false; };
  }, [user]);

  return isReviewer;
}

export type ReviewScope = "post" | "impact_media" | "impact_update";

export async function reviewMedia(scope: ReviewScope, id: string, state: ReviewState, note?: string) {
  const { error } = await supabase.rpc("review_media", {
    _scope: scope,
    _id: id,
    _state: state,
    _note: note?.trim() ? note.trim() : undefined,
  });
  if (error) throw new Error(error.message);
}

export type ReviewQueueItem = {
  scope: ReviewScope;
  id: string;
  url: string | null;
  poster_url: string | null;
  duration_seconds: number | null;
  review_state: ReviewState;
  review_note: string | null;
  created_at: string;
  context_id: string;
  context_title: string | null;
  author_id: string | null;
  author_name: string | null;
  author_username: string | null;
};

export async function fetchReviewQueue(state: ReviewState = "pending") {
  const { data, error } = await supabase.rpc("media_review_queue", { _state: state, _limit: 60 });
  if (error) throw new Error(error.message);
  return (data ?? []) as ReviewQueueItem[];
}
